/**
 * Date tile parts in the venue time zone (docs/UX-REDESIGN.md §4).
 * The visible tile shows a short weekday, the day of the month, and a start
 * time. The accessible name is the full weekday, month, day, and clock time.
 */

export interface DateTileParts {
  weekday: string;
  day: string;
  time: string;
  accessibleName: string;
}

/** Venue-local calendar day, `YYYY-MM-DD`. */
function venueDay(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

/** Add calendar days to a `YYYY-MM-DD` string. Not a 24-hour shift. */
function addCalendarDays(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number);
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function dateTileParts(
  startInstant: string,
  timeZone: string,
  now: Date = new Date(),
): DateTileParts {
  const date = new Date(startInstant);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  const weekdayLong = value('weekday');
  const month = value('month');
  const day = value('day');
  const hour = value('hour');
  const minute = value('minute');
  const dayPeriod = value('dayPeriod');
  const shortWeekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' })
    .format(date)
    .toUpperCase();
  const startDay = venueDay(date, timeZone);
  const today = venueDay(now, timeZone);
  const weekday =
    startDay === today
      ? 'Today'
      : startDay === addCalendarDays(today, 1)
        ? 'Tomorrow'
        : shortWeekday;
  const clock = minute === '00' ? `${hour} ${dayPeriod}` : `${hour}:${minute} ${dayPeriod}`;
  return {
    weekday,
    day,
    time: clock.trim(),
    accessibleName: `${weekdayLong}, ${month} ${day}, ${hour}:${minute} ${dayPeriod}`
      .replace(/\s+/g, ' ')
      .trim(),
  };
}
